import { SetFeatureFlags } from "../../../types/decisions";
import { definitions } from "../../../types/supabase";
import { ScheduleType } from "../../../utils/schedule/calculateSchedule";

export type CMSCollection<T> = {
  entry: any;
  resources: T[];
};

type BaseResource = definitions["resources"];

export type CMSBullets = CMSCollection<CMSRichText>;

export type CMSImageResource = {
  type: "image_resource";
} & BaseResource;

export type CMSImageWithCaption = {
  type: "image_with_caption";
  image: CMSImageResource;
  caption: string;
} & BaseResource;

export type CMSCollectionAsSingleEntity = {
  type: "collection_as_single_entity";
  metadata: {
    value: Array<{ id: string; table_name: string }>;
  };
} & BaseResource;

export type CMSLink = {
  type: "link";
  title: string;
  href: string;
} & BaseResource;

export type CMSRichText = {
  type: "richtext";
} & BaseResource;

export type CMSRGBColor = {
  type: "rgb_color";
  primaryRgbColor: string;
  secondaryRgbColor: string;
  tertiaryRgbColor: string;
  quaternaryRgbColor: string;
};

export type CMSText = {
  type: "text";
} & BaseResource;

export type CMSSideBySide = {
  type: "side_by_side_comparison";
  id: string;
  subtitle: string;
  title: string;
  price: string;
  primaryBullets: string;
  secondaryBullets: string;
  strikethroughBullets: string;
};

export type CMSTestimonial = {
  type: "testimonial";
  id: string;
  quote: string;
  name: string;
} & BaseResource;

type ResourceType = definitions["resources"]["type"];
export type ComponentRequiredData = {
  type: ResourceType;
  tags: Array<unknown>;
  retrieveAll?: boolean;
  exampleQuantity?: number;
  metadata?: {
    onClick: string;
    onClickArgs?: string;
  };
  rules?: {
    minLength?: number;
    maxLength?: number;
  };
};
export type ComponentDefinition = {
  type: string;
  version: number;
  required_data: {
    [propName: string]: ComponentRequiredData;
  };
  metadata?: {
    id?: string;
    hidden?: boolean;
  };
};

export type CMSVideoResource = {
  type: "video_resource";
  metadata?: any;
} & BaseResource;

export type CMSSchedule = {
  type: "schedule";
  metadata: {
    value: ScheduleType;
  };
  value?: ScheduleType;
  options: {
    inTimezone: "local" | "EST";
  };
} & BaseResource;

export type CMSPageId = string;
export type CMSPageIdResource = {
  type: CMSPageId;
  metadata: {
    value: string;
  };
};

export type CMSPointsOfInterestResource = {
  type: "points_of_interest";
  metadata: {
    type: "chapters_seen" | "interactive_content" | "auto_pause";
    data: {
      buyButton?: {
        starts_at: number;
        ends_at?: number;
        metadata: {
          url: string;
        };
      };

      [key: string]:
        | {
            starts_at: number;
            ends_at?: number;
            metadata: {};
          }
        | undefined
        | Array<{
            starts_at: number;
            metadata?: {
              type: "text";
              resourceIds: string[];
            };
          }>;
    };
  };
};

export interface CMSAutoPause extends CMSPointsOfInterestResource {
  metadata: {
    type: "auto_pause";
    data: {
      pauses: Array<{
        id: number;
        starts_at: number;
      }>;
    };
  };
}

export interface CMSChaptersSeen extends CMSPointsOfInterestResource {
  metadata: {
    type: "chapters_seen";
    data: {
      [key: string]: {
        starts_at: number;
        ends_at?: number;
        metadata: {};
      };
    };
  };
}

export type BuyButtonInteractivePointOfInterest = {
  starts_at: number;
  ends_at?: number;
  metadata: {
    url: string;
  };
};

export interface CMSInteractivePointsOfInterestContent
  extends CMSPointsOfInterestResource {
  metadata: {
    type: "interactive_content";
    data: {
      buyButton?: BuyButtonInteractivePointOfInterest;
    };
  };
}

export type CMSWebinarResource = {
  type: "webinar";
  video: CMSVideoResource;
  schedule: CMSSchedule;
  messages: CMSCollectionAsSingleEntity;
  chapters_seen: CMSChaptersSeen;
  interactive_content: CMSInteractivePointsOfInterestContent;
  tagline: CMSText;
  waitingRoomImage: CMSImageResource;
} & BaseResource;

export type CMSOffer = {
  type: "offer";
  id: string;
  paymentPrompt: string;
  msrpPrice: string;
  offerPrice: string;
  ctaText: string;
  offer: definitions["offers"] & { packages: definitions["packages"] };
} & BaseResource;

export type CMSDisplayOffer = {
  type: "display_offer";
  offer: definitions["offers"] & { packages: definitions["packages"] };
  primary_products: Array<
    definitions["products"] & { image: CMSImageResource }
  >;
  secondary_products: Array<
    definitions["products"] & { image: CMSImageResource }
  >;
} & BaseResource;

export type CMSQuestionAndAnswer = {
  type: "questionAndAnswer";
  question: CMSText;
  answer: CMSRichText;
} & BaseResource;

export type CMSImageTitleAndText = {
  resources: any;
  supportingText: any;
  type: "imageTitleAndText";
  image: CMSImageResource;
  title: string;
  text: string;
  name: string;
} & BaseResource;

export const onClickActions: { [key: string]: Function } = {
  consoleLog: (emitEvent: any, args: any) => console.log("clicked!", args),
  showModal: (emitEvent: any, args: any) => {
    emitEvent("showModal", args);
  },
};

export type CMSStatsWithImageAndCTA = {
  type: "stats_with_image_and_cta";
  image: CMSImageResource;
  ctaText: string;
  ctaLink: string;
  title: string;
  description: string;
  stats: {
    difficultyLevel: string;
    lessonCount: string;
    courseCount: string;
    physicalKitNumber: string;
    traditionalPrice: string;
    savingsVsMsrp: string;
  };
  bonusLabel: string;
  bonusList: string;
  lastBonusNote: string;
} & BaseResource;

export type HasOnClick<T> = T & {
  metadata: {
    onClick: keyof typeof onClickActions;
  };
};

export type InjectableComponentProps<T> = T & {
  component: ComponentDefinition;
  brand: definitions["brands"];
  brandAbbreviation: "piaf" | "nma";
  brandConfig: definitions["brand_configurations"];
  stripePublishableKey: string;
  clientSideId: string;
  isDevelopmentMode: boolean;
  apiUrl: string;
  featureFlags: SetFeatureFlags;
  featureFlagHash?: string;
};
